// Shared rendering primitives for heatmap-style canvas controllers.
//
// Both multi_heatmap_controller.js (sibling Hourly Heatmap) and
// coverage_gap_heatmap_controller.js render a Day x Hour matrix on a
// canvas. These helpers encode the geometry (cell rect, axis labels)
// so both controllers can render against the same coordinate system
// without duplicating math.
//
// IMPORTANT — divergence note for future maintainers:
// `multi_heatmap_controller` renders via Chart.js (`type: "matrix"` and
// `type: "bar"`). Chart.js owns its own geometry + axis labels via the
// `chartArea` callbacks and the category-scale tick renderer. As a
// result, the only helper `multi_heatmap_controller` consumes from this
// module is `formatHourLabel` (the one truly behavior-equivalent
// primitive). `buildGridGeometry` and `drawAxisLabels` are designed for
// raw 2D-context canvas controllers (e.g. the Coverage Gap heatmap)
// that do not delegate layout to Chart.js. Do NOT retrofit Chart.js
// controllers onto these geometry helpers without first refactoring the
// rendering pipeline end-to-end — the visual output will diverge.

// LRPO-5388 — mirrors the sibling multi-heatmap's maxTicksLimit: the most
// day labels that stay readable before thinning kicks in.
export const MAX_DAY_AXIS_LABELS = 16

export function formatHourLabel(hour) {
  if (hour === 0) return "12 AM"
  if (hour === 12) return "12 PM"
  if (hour < 12) return `${hour} AM`
  return `${hour - 12} PM`
}

// Returns an object describing the cell-rect geometry given the canvas
// dimensions and the day/hour grid. All callers consume the same shape.
export function buildGridGeometry({ canvasWidth, canvasHeight, days, hours, padding = { top: 32, left: 64, right: 16, bottom: 24 } }) {
  const usableWidth  = canvasWidth  - padding.left - padding.right
  const usableHeight = canvasHeight - padding.top  - padding.bottom
  const cellWidth    = days.length  > 0 ? usableWidth  / days.length  : 0
  const cellHeight   = hours.length > 0 ? usableHeight / hours.length : 0

  return {
    padding,
    cellWidth,
    cellHeight,
    cellRectFor(dayIdx, hour) {
      const hourIdx = hours.indexOf(hour)
      if (hourIdx < 0) return null
      return {
        x: padding.left + dayIdx * cellWidth,
        y: padding.top  + hourIdx * cellHeight,
        w: cellWidth,
        h: cellHeight
      }
    }
  }
}

// Draws axis labels — days along the top, hours down the left side.
export function drawAxisLabels(ctx, { days, hours, geometry, fontFamily = "system-ui, sans-serif" }) {
  ctx.save()
  ctx.font = `12px ${fontFamily}`
  ctx.fillStyle = "#1a1a1a"
  ctx.textAlign = "center"
  ctx.textBaseline = "bottom"

  // LRPO-5388 — a ~130-day event drew a label per column and they overprinted
  // into an unreadable smear. Thin the LABELS exactly like the sibling
  // multi-heatmap's axis (autoSkip, max ~16 ticks): every day keeps its
  // column, cells and drilldown; days are sequential so a reader infers the
  // ones not drawn. Step from the first day so Day 1 is always labelled.
  const labelStep = Math.max(1, Math.ceil(days.length / MAX_DAY_AXIS_LABELS))
  days.forEach((day, idx) => {
    if (idx % labelStep !== 0) return
    const x = geometry.padding.left + (idx + 0.5) * geometry.cellWidth
    ctx.fillText(day.day_num,  x, geometry.padding.top - 14)
    ctx.fillText(day.day_date, x, geometry.padding.top - 2)
  })

  // Hour labels down the left side
  ctx.textAlign = "right"
  ctx.textBaseline = "middle"
  hours.forEach((hour, idx) => {
    const y = geometry.padding.top + (idx + 0.5) * geometry.cellHeight
    ctx.fillText(formatHourLabel(hour), geometry.padding.left - 8, y)
  })

  ctx.restore()
}
